import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { processReferral } from "./referralService";

/**
 * Save or update user wallet address
 * @param {string} address - Wallet address
 * @param {object} additionalData - Additional user data
 * @param {string} referralCode - Optional referral code from URL
 * @returns {Promise<object>} User document data
 */
export const saveUserWallet = async (address, additionalData = {}, referralCode = null) => {
  try {
    if (!address) {
      throw new Error("Wallet address is required");
    }

    // Normalize address to lowercase
    const normalizedAddress = address.toLowerCase();

    // Get existing user document
    const userRef = doc(db, "users", normalizedAddress);
    const userDoc = await getDoc(userRef);

    const now = serverTimestamp();
    const userData = {
      address: normalizedAddress,
      updatedAt: now,
      ...additionalData,
    };

    const isNewUser = !userDoc.exists();

    if (userDoc.exists()) {
      // Update existing user - ensure totalPoints exists
      const existingData = userDoc.data();
      if (typeof existingData.totalPoints !== 'number') {
        userData.totalPoints = existingData.totalPoints || 0;
      }
      await updateDoc(userRef, userData);
    } else {
      // Create new user - initialize totalPoints to 0 if not provided
      await setDoc(userRef, {
        ...userData,
        totalPoints: additionalData.totalPoints !== undefined ? additionalData.totalPoints : 0,
        createdAt: now,
        firstConnectedAt: now,
      });
    }

    // Process referral if this is a new user and a referral code was provided
    if (isNewUser && referralCode) {
      try {
        await processReferral(normalizedAddress, referralCode);
      } catch (referralError) {
        // Don't fail user creation if referral processing fails
        console.error("Error processing referral:", referralError);
      }
    }

    const finalUserData = userDoc.exists() 
      ? { id: normalizedAddress, ...userDoc.data(), ...userData }
      : { id: normalizedAddress, ...userData };

    return finalUserData;
  } catch (error) {
    console.error("Error saving user wallet:", error);
    throw error;
  }
};

/**
 * Get user data by wallet address
 * @param {string} address - Wallet address
 * @returns {Promise<object|null>} User document or null if not found
 */
export const getUserByAddress = async (address) => {
  try {
    if (!address) {
      return null;
    }

    const normalizedAddress = address.toLowerCase();
    const userRef = doc(db, "users", normalizedAddress);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }

    return null;
  } catch (error) {
    console.error("Error getting user by address:", error);
    throw error;
  }
};

